/*
 * @providesModule FTUETournamentController
 */

import React from 'react';

import APIUtilities from 'APIUtilities';
import ClientPropertyConstants from 'ClientPropertyConstants';
import ErrorDialogModal from 'ErrorDialogModal';
import {_m} from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import MatchJoinController from 'MatchJoinController';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import PlayTabStore from 'PlayTabStore';
import PlayableMatchesStore from 'PlayableMatchesStore';
import {SKZGameMode} from 'SKZGameMode';
import Match from 'SKZMatch';
import SkillzRouter from 'SkillzRouter';
import SKZTournament, {TournamentRecord as SKZTournamentRecord} from 'SKZTournament';
import SKZTournamentCategories, {SKZTournamentCategoryRecord} from 'SKZTournamentCategories';
import SKZTournamentTemplateSet from 'SKZTournamentTemplateSet';
import Sizes from 'Sizes';
import SyncFTUEController from 'SyncFTUEController';
import VersusCoordinator from 'VersusCoordinator';
import {VersusProps} from 'Versus';
import {NativeModules} from 'react-native';
import {Navigator} from 'react-native-deprecated-custom-components';
import GlobalEventEmitter from 'GlobalEventEmitter';

export type FTUETournamentsResponse = {
  async: SKZTournamentRecord | null;
  sync: {gameMode: SKZGameMode; tournament: SKZTournament} | null;
};

const HEAD_TO_HEAD_CATEGORY = 'H2H';
const SkillzModule = NativeModules.SkillzModule;
const FTUE_TYPE = {
  ASYNC: 'Async',
  SYNC: 'Sync',
  HYBRID: 'Hybrid',
};

type FTUEMatchOptions = {
  navigator?: Navigator;
  versusProps?: Partial<VersusProps>;
};

type FTUEJoinOptions = {
  /**
   * Optional transaction ID for the join request; if undefined, a UUID is generated
   */
  transactionID?: string;

  /**
   * Whether to show the LoadingIndicator during the match join process
   */
  showLoading?: boolean;

  /**
   * Optional callback to trigger when a match has been joined for the given
   * tournament
   */
  onJoinMatch?: (match: Match) => void | Promise<void>;

  /**
   * Optional function to determine whether the Versus screen should be shown;
   * if undefined, the Versus screen is shown by default
   */
  shouldShowVersus?: (match: Match) => boolean;

  /**
   * Optional function used to get props to pass to the Versus screen based on
   * the joined match; if shouldShowVersus returns false, this is not called
   */
  getVersusProps?: (match: Match) => Partial<VersusProps>;
};

class FTUETournamentController {
  getTournamentsForFirstFTUEMatch(userBalance: number): Promise<FTUETournamentsResponse> {
    if (!ClientPropertyConstants.isCoreLoopTungstenEnabled()) {
      return this.getTournamentsForFirstFTUEMatchLegacy(userBalance);
    }

    return new Promise<FTUETournamentsResponse>(
      (resolve: (tournaments: FTUETournamentsResponse) => void, reject: (error: any) => void) => {
        PlayTabStore.getGroupedTournamentTemplates()
          .then((response: SKZTournamentCategories) => {
            if (!response) {
              reject('FTUE: no tournament categories found');
              return;
            }
            // @ts-ignore: No longer using CoreLoopTungstenEnabled. Fix TS errors if it's ever brought back
            const responseTournamentCategories = new SKZTournamentCategories(response);
            // @ts-ignore: No longer using CoreLoopTungstenEnabled. Fix TS errors if it's ever brought back
            const h2hSection =
              responseTournamentCategories.getCategoryForType(HEAD_TO_HEAD_CATEGORY);

            const h2hGameModes = h2hSection?.game_modes ?? null;
            const asyncTournament = this._getFirstAsyncTournament(h2hGameModes, userBalance);
            const syncTournament = this._getFirstSyncTournament(
              responseTournamentCategories,
              userBalance
            );

            const isAsyncFTUEEnabled = ClientPropertyConstants.getFTUEType() == FTUE_TYPE.ASYNC;
            const isSyncFTUEEnabled = ClientPropertyConstants.getFTUEType() == FTUE_TYPE.SYNC;
            const isHybridFTUEEnabled = ClientPropertyConstants.getFTUEType() == FTUE_TYPE.HYBRID;

            const isAsyncFTUEValid = asyncTournament && isAsyncFTUEEnabled;
            const isSyncFTUEValid = syncTournament && isSyncFTUEEnabled;
            const isHybridFTUEValid = asyncTournament && syncTournament && isHybridFTUEEnabled;

            if (!isAsyncFTUEValid && !isSyncFTUEValid && !isHybridFTUEValid) {
              reject('FTUE: no valid tournament user could afford for first game');
              return;
            }

            resolve({
              async: asyncTournament,
              sync: syncTournament,
            });
          })
          .catch((error: any) => {
            reject(error);
          });
      }
    );
  }

  getTournamentsForFirstFTUEMatchLegacy(userBalance: number): Promise<FTUETournamentsResponse> {
    return new Promise(
      (resolve: (tournament: FTUETournamentsResponse) => void, reject: (error: any) => void) => {
        PlayableMatchesStore.fetchTournamentCategories(false)
          .then((response: SKZTournamentCategoryRecord[]) => {
            if (!response) {
              reject('FTUE: no tournament categories found');
              return;
            }

            const asyncTournament = this._getFirstAsyncTournamentLegacy(response, userBalance);
            const syncTournament = this._getFirstSyncTournamentLegacy(response, userBalance);

            const isAsyncFTUEEnabled = ClientPropertyConstants.getFTUEType() == FTUE_TYPE.ASYNC;
            const isSyncFTUEEnabled = ClientPropertyConstants.getFTUEType() == FTUE_TYPE.SYNC;
            const isHybridFTUEEnabled = ClientPropertyConstants.getFTUEType() == FTUE_TYPE.HYBRID;

            const isAsyncFTUEValid = asyncTournament && isAsyncFTUEEnabled;
            const isSyncFTUEValid = syncTournament && isSyncFTUEEnabled;
            const isHybridFTUEValid = asyncTournament && syncTournament && isHybridFTUEEnabled;

            if (!isAsyncFTUEValid && !isSyncFTUEValid && !isHybridFTUEValid) {
              reject('FTUE: no valid tournament user could afford for first game');
              return;
            }

            resolve({
              async: asyncTournament,
              //Note we are mapping the old logic to the new game mode. the fact we are populating this with empty strings should have no affect
              sync: syncTournament
                ? {
                    gameMode: new SKZGameMode({
                      id: '',
                      name: '',
                      mode_rules_markdown: '',
                      background_image_url: '',
                      tournaments: syncTournament?.templateSet.tournaments,
                    }),
                    tournament: syncTournament?.tournament,
                  }
                : null,
            });
          })
          .catch((error: any) => {
            reject(error);
          });
      }
    );
  }

  _getFirstAsyncTournamentLegacy(
    tournaments: SKZTournamentCategoryRecord[],
    balance: number
  ): SKZTournamentRecord | null {
    const practiceTournaments = tournaments.find(
      (category: SKZTournamentCategoryRecord): boolean => {
        return category.type === 'Z';
      }
    );

    if (!practiceTournaments || !practiceTournaments.tournaments?.length) {
      console.warn('FTUE: no practice tournaments');
      return null;
    }

    // Limited Use Tournaments may exist in Practice, remove those for FTUE
    const zTournaments = practiceTournaments.tournaments.filter((tournament) => {
      return !tournament.limited_use;
    });

    zTournaments.sort((a: SKZTournamentRecord, b: SKZTournamentRecord): number => {
      return a.z_fee - b.z_fee;
    });
    const cheapestTournament = zTournaments[0];
    if (!cheapestTournament) {
      console.warn('FTUE: no practice tournaments');
      return null;
    }

    if (cheapestTournament.z_fee > balance) {
      console.warn('FTUE: too expensive');
      return null;
    }

    return cheapestTournament;
  }

  _getFirstSyncTournamentLegacy(
    tournaments: SKZTournamentCategoryRecord[],
    balance: number
  ): {templateSet: SKZTournamentTemplateSet; tournament: SKZTournament} | null {
    const firstSyncTournament = SyncFTUEController.getTournamentForSyncFTUELegacy(
      new SKZTournamentCategories({categories: tournaments})
    );

    if (!firstSyncTournament) {
      console.warn('FTUE: no sync ftue tournament found');
      return null;
    }

    if (balance < firstSyncTournament.tournament.z_fee) {
      console.warn('FTUE: sync tournament too expensive');
      return null;
    }

    return firstSyncTournament;
  }

  _getFirstAsyncTournament(
    gameModes: SKZGameMode[] | null,
    balance: number
  ): SKZTournamentRecord | null {
    const zTournaments: Array<SKZTournament> = [];

    // Make a list of all Z tournaments from the list of game modes
    gameModes?.forEach((gameMode: SKZGameMode) => {
      gameMode.tournaments?.forEach((tournament: SKZTournament) => {
        if (tournament.isZTournament()) {
          zTournaments.push(tournament);
        }
      });
    });
    if (!zTournaments || !zTournaments?.length) {
      console.warn('FTUE: no z tournaments');
      return null;
    }

    // Sort the tournaments list to find the first, cheapest template
    zTournaments.sort((a: SKZTournamentRecord, b: SKZTournamentRecord): number => {
      return a.z_fee - b.z_fee;
    });
    const cheapestTournament = zTournaments[0];
    if (cheapestTournament.z_fee > balance) {
      console.warn('FTUE: too expensive');
      return null;
    }

    if (!cheapestTournament) {
      console.warn('FTUE: no practice tournaments');
      return null;
    }

    return cheapestTournament;
  }

  _getFirstSyncTournament(
    tournamentCategories: SKZTournamentCategories,
    balance: number
  ): {gameMode: SKZGameMode; tournament: SKZTournament} | null {
    const templateSets = tournamentCategories.getCategoryForType('SYNC')?.tournament_template_sets;

    if (!templateSets || templateSets.length === 0) {
      return null;
    }

    // @ts-ignore: No longer using CoreLoopTungstenEnabled. Fix TS errors if it's ever brought back
    const firstSyncTournament = SyncFTUEController.getTournamentForSyncFTUE(templateSets);

    if (!firstSyncTournament) {
      console.warn('FTUE: no sync ftue tournament found');
      return null;
    }

    if (balance < firstSyncTournament.tournament.z_fee) {
      console.warn('FTUE: sync tournament too expensive');
      return null;
    }

    return firstSyncTournament;
  }

  showNoAvailableTournamentsError(onRetry: () => void, error) {
    const modal = (
      <ErrorDialogModal
        onCancel={() => {
          NativeModules.SkillzModule.exitSkillz();
        }}
        retryCallback={onRetry}
        onDone={() => ModalNavigationController.hideModal()}
        message={_m('Unable to fetch tournaments, please try again later.')}
        error={error}
      />
    );

    ModalNavigationController.showModal(modal);
  }

  displayVersusForFTUEMatch(firstFTUETournament: SKZTournamentRecord, options?: FTUEMatchOptions) {
    const versusProps = {
      allowExitSkillz: false,
      canGoBack: false,
      tournament: firstFTUETournament,
      matchCode: null,
      viewedAd: false,
      isFTUE: true,
      isPaused: true,
      onDone: () => {
        /* */
      },
      ...options?.versusProps,
    };

    SkillzRouter.replace(
      `/games/${SkillzModule.App.GameId as string}/versus/${firstFTUETournament.id}`,
      versusProps
    );
  }

  async startFTUEMatch(
    tournament: SKZTournament,
    {
      transactionID,
      showLoading = true,
      onJoinMatch,
      shouldShowVersus,
      getVersusProps,
    }: FTUEJoinOptions = {}
  ) {
    // Generate a transaction ID for the join request if needed
    transactionID = transactionID ?? (await APIUtilities.createUUID());

    if (showLoading) {
      LoadingIndicator.show();
    }

    MatchJoinController.initiateTournamentJoinWithTxnID(
      tournament,
      null,
      false,
      null,
      false,
      transactionID,
      async (error, match) => {
        if (error || !match) {
          console.warn('FTUE: error joining tournament', error);
          if (showLoading) LoadingIndicator.hide();
          GlobalEventEmitter.emit('EndFirstFTUEMatch');
          return;
        }

        await onJoinMatch?.(match);

        const onBeginMatch = () =>
          MatchJoinController.passControlToPublisherForJoin(tournament, match);

        if (shouldShowVersus?.(match) ?? true) {
          console.warn(
            'FTUE: WARNING: WE DO NOT SUPPORT Versus.onBeginMatch, THIS FLOW WONT WORK',
            error
          );
          // The below code is not supported, and hasn't been in user
          // in production for a while. Migrating to Solito/ReactNavigation,
          // we can no longer pass functions as props, breaking this onBeginMatch.
          // We should remove this code when time allows.
          const versusProps = getVersusProps?.(match) ?? {};
          this.displayVersusForFTUEMatch(tournament, {
            versusProps: {
              match,
              ...versusProps,
              onBeginMatch: () => {
                versusProps.onBeginMatch?.();
                onBeginMatch();
              },
            },
          });
        } else {
          onBeginMatch();
        }

        if (showLoading) LoadingIndicator.hide();
      }
    );
  }
}

export default new FTUETournamentController();
